import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsResourcesOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Top utility bar - navy */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#003366] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-10 text-xs">
            <div className="flex items-center space-x-6">
              <span className="flex items-center space-x-1.5">
                <i className="fas fa-map-marker-alt text-[#F5A623]"></i>
                <span>4525 S Sandhill Rd, Las Vegas, NV 89121</span>
              </span>
              <span className="hidden md:flex items-center space-x-1.5">
                <i className="fas fa-clock text-[#F5A623]"></i>
                <span>Mon–Fri 9AM–5PM &nbsp;|&nbsp; Sat 10AM–3PM</span>
              </span>
            </div>
            <div className="flex items-center space-x-5">
              <span className="hidden sm:flex items-center space-x-1.5 text-[#F5A623] font-semibold">
                <i className="ri-shield-check-line"></i>
                <span>Licensed 3rd Party Nevada DMV Provider</span>
              </span>
              <a href="tel:7029797202" className="flex items-center space-x-1.5 font-bold text-[#F5A623] hover:text-yellow-300 transition-colors whitespace-nowrap">
                <i className="fas fa-phone text-xs"></i>
                <span>702.979.7202</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main header - white */}
      <header className="fixed left-0 right-0 z-40 top-10 bg-white border-b-2 border-[#003366] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">

            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group flex-shrink-0">
              <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 bg-[#003366] rounded">
                <i className="fas fa-car text-[#F5A623] text-lg"></i>
              </div>
              <div className="leading-tight">
                <p className="font-bold text-[#003366] text-base tracking-tight">Speedy DMV Services</p>
                <p className="text-[#F5A623] text-xs font-semibold tracking-wide uppercase">Las Vegas, Nevada</p>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center space-x-1">
              <Link to="/" className={`px-3 py-2 text-sm font-semibold rounded transition-colors whitespace-nowrap ${isActive('/') ? 'text-[#003366] bg-blue-50' : 'text-[#333] hover:text-[#003366] hover:bg-gray-50'}`}>Home</Link>

              {/* Services dropdown */}
              <div className="relative group">
                <button className="flex items-center space-x-1 px-3 py-2 text-sm font-semibold text-[#333] hover:text-[#003366] hover:bg-gray-50 rounded transition-colors whitespace-nowrap">
                  <span>Services</span>
                  <i className="ri-arrow-down-s-line text-sm transition-transform group-hover:rotate-180 duration-200"></i>
                </button>
                <div className="absolute top-full left-0 mt-0 w-56 bg-white border border-gray-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                  {[
                    { to: '/services', label: 'All Services', icon: 'ri-layout-grid-line' },
                    { to: '/vehicle-registration', label: 'Vehicle Registration', icon: 'ri-car-line' },
                    { to: '/title-transfer', label: 'Title Transfer', icon: 'ri-file-transfer-line' },
                    { to: '/license-plates', label: 'License Plates', icon: 'ri-price-tag-3-line' },
                    { to: '/duplicate-title', label: 'Duplicate Title', icon: 'ri-file-copy-line' },
                    { to: '/temporary-permits', label: 'Temporary Permits', icon: 'ri-time-line' },
                    { to: '/notary-services', label: 'Notary Services', icon: 'ri-quill-pen-line' },
                  ].map(item => (
                    <Link key={item.to} to={item.to} className="flex items-center space-x-3 px-4 py-2.5 text-sm text-[#333] hover:bg-[#003366] hover:text-white transition-colors border-b border-gray-100 last:border-0">
                      <i className={`${item.icon} text-[#F5A623] w-4`}></i>
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Resources dropdown */}
              <div className="relative group">
                <button className="flex items-center space-x-1 px-3 py-2 text-sm font-semibold text-[#333] hover:text-[#003366] hover:bg-gray-50 rounded transition-colors whitespace-nowrap">
                  <span>DMV Resources</span>
                  <i className="ri-arrow-down-s-line text-sm transition-transform group-hover:rotate-180 duration-200"></i>
                </button>
                <div className="absolute top-full left-0 mt-0 w-60 bg-white border border-gray-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                  {[
                    { to: '/nevada-dmv-info', label: 'DMV Info Hub', icon: 'ri-information-line' },
                    { to: '/nevada-dmv-forms', label: 'Nevada DMV Forms', icon: 'ri-file-list-3-line' },
                    { to: '/nevada-vehicle-registration-guide', label: 'Registration Guide', icon: 'ri-car-line' },
                    { to: '/nevada-title-transfer-guide', label: 'Title Transfer Guide', icon: 'ri-file-transfer-line' },
                    { to: '/nevada-smog-emissions-guide', label: 'Emissions Guide', icon: 'ri-leaf-line' },
                    { to: '/nevada-registration-fees', label: 'Fee Guide', icon: 'ri-money-dollar-circle-line' },
                    { to: '/new-resident', label: 'New to Nevada', icon: 'ri-map-pin-line' },
                    { to: '/required-documents', label: 'Required Documents', icon: 'ri-file-text-line' },
                  ].map(item => (
                    <Link key={item.to} to={item.to} className="flex items-center space-x-3 px-4 py-2.5 text-sm text-[#333] hover:bg-[#003366] hover:text-white transition-colors border-b border-gray-100 last:border-0">
                      <i className={`${item.icon} text-[#F5A623] w-4`}></i>
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <Link to="/fees" className={`px-3 py-2 text-sm font-semibold rounded transition-colors whitespace-nowrap ${isActive('/fees') ? 'text-[#003366] bg-blue-50' : 'text-[#333] hover:text-[#003366] hover:bg-gray-50'}`}>Fees</Link>
              <Link to="/faq" className={`px-3 py-2 text-sm font-semibold rounded transition-colors whitespace-nowrap ${isActive('/faq') ? 'text-[#003366] bg-blue-50' : 'text-[#333] hover:text-[#003366] hover:bg-gray-50'}`}>FAQ</Link>
              <Link to="/about" className={`px-3 py-2 text-sm font-semibold rounded transition-colors whitespace-nowrap ${isActive('/about') ? 'text-[#003366] bg-blue-50' : 'text-[#333] hover:text-[#003366] hover:bg-gray-50'}`}>About</Link>
              <Link to="/contact" className={`px-3 py-2 text-sm font-semibold rounded transition-colors whitespace-nowrap ${isActive('/contact') ? 'text-[#003366] bg-blue-50' : 'text-[#333] hover:text-[#003366] hover:bg-gray-50'}`}>Contact</Link>
            </nav>

            {/* CTA buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <Link to="/appointment" className="px-4 py-2 text-sm font-semibold border-2 border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white transition-colors rounded whitespace-nowrap">
                Book Appointment
              </Link>
              <a href="tel:7029797202" className="flex items-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-4 py-2 rounded font-bold text-sm transition-colors whitespace-nowrap">
                <i className="fas fa-phone text-xs"></i>
                <span>702.979.7202</span>
              </a>
            </div>

            {/* Mobile hamburger */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden w-9 h-9 flex items-center justify-center border border-gray-300 rounded text-[#003366]">
              <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-base`}></i>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`lg:hidden bg-white border-t border-gray-200 transition-all duration-300 overflow-hidden ${isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="px-4 py-4 space-y-1">
            {[{ to: '/', label: 'Home' }, { to: '/about', label: 'About' }, { to: '/fees', label: 'Fees' }, { to: '/faq', label: 'FAQ' }, { to: '/contact', label: 'Contact' }].map(item => (
              <Link key={item.to} to={item.to} className="block py-2.5 px-3 text-[#333] hover:text-[#003366] hover:bg-blue-50 rounded font-medium text-sm">{item.label}</Link>
            ))}
            <div>
              <button onClick={() => setIsServicesOpen(!isServicesOpen)} className="flex items-center justify-between w-full py-2.5 px-3 text-[#333] rounded font-medium text-sm">
                <span>Services</span>
                {isServicesOpen ? <i className="ri-arrow-up-s-line text-[#F5A623]"></i> : <i className="ri-arrow-down-s-line text-[#F5A623]"></i>}
              </button>
              {isServicesOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-[#F5A623] pl-3">
                  {[{ to: '/services', label: 'All Services' }, { to: '/vehicle-registration', label: 'Vehicle Registration' }, { to: '/title-transfer', label: 'Title Transfer' }, { to: '/license-plates', label: 'License Plates' }, { to: '/duplicate-title', label: 'Duplicate Title' }, { to: '/temporary-permits', label: 'Temporary Permits' }, { to: '/notary-services', label: 'Notary Services' }].map(item => (
                    <Link key={item.to} to={item.to} className="block py-1.5 text-[#003366] text-sm">{item.label}</Link>
                  ))}
                </div>
              )}
            </div>
            <div>
              <button onClick={() => setIsResourcesOpen(!isResourcesOpen)} className="flex items-center justify-between w-full py-2.5 px-3 text-[#333] rounded font-medium text-sm">
                <span>DMV Resources</span>
                {isResourcesOpen ? <i className="ri-arrow-up-s-line text-[#F5A623]"></i> : <i className="ri-arrow-down-s-line text-[#F5A623]"></i>}
              </button>
              {isResourcesOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-[#F5A623] pl-3">
                  {[{ to: '/nevada-dmv-info', label: 'DMV Info Hub' }, { to: '/nevada-dmv-forms', label: 'Nevada DMV Forms' }, { to: '/new-resident', label: 'New to Nevada' }, { to: '/required-documents', label: 'Required Documents' }].map(item => (
                    <Link key={item.to} to={item.to} className="block py-1.5 text-[#003366] text-sm">{item.label}</Link>
                  ))}
                </div>
              )}
            </div>
            <div className="pt-3 space-y-2">
              <Link to="/appointment" className="block w-full text-center border-2 border-[#003366] text-[#003366] py-2.5 rounded font-semibold text-sm">Book Appointment</Link>
              <a href="tel:7029797202" className="flex items-center justify-center space-x-2 w-full bg-[#F5A623] text-white py-3 rounded font-bold text-sm">
                <i className="fas fa-phone text-xs"></i><span>Call 702.979.7202</span>
              </a>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
